import React, {
  useState,
  useEffect,
  useRef,
  MutableRefObject,
  useContext,
} from "react";
import { useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  MdCallReceived,
  MdOutlineArticle,
  MdOutlineAutoAwesome,
  MdOutlineEdit,
  MdFastRewind,
  MdClose,
  MdUploadFile,
  MdDownload,
  MdOutlineSave,
  MdFastForward,
  MdRestore,
  MdOutlineDarkMode,
  MdOutlineLightMode,
} from "react-icons/md";
import Video from "components/Editor/utils/Video";
import Player from "@vimeo/player";
import AssistantPopup from "../assistant/AssistantPopup";
import { AssistantAllButtonTypes, CodeFile } from "utils/interfaces";
import PopupContainer from "./utils/PopupContainer";
import { fetchVideoData, getScratchFileFromAWS } from "services/filesRequests";
import GlowingButton from "../utils/GlowingButton";
import InstructionsButton from "./utils/InstructionsButton";
import OnboardingInstructions from "./onboarding-instructions/OnboardingInstructions";
import {
  EditorContext,
  ScratchEnvironmentContext,
} from "components/utils/Contexts";
import { usePlatform } from "components/utils/PlatformContext";
import useElementSize from "hooks/ElementSize";
import { postScratchRequest } from "./utils/postScratchRequest";
import DropdownMenu from "components/main-view/utils/components/dropdown-menu";

interface ScratchEditorHeaderProps {
  name: string;
  video: string;
  activeItem: "editor" | "assistant";
  setActiveItem: React.Dispatch<React.SetStateAction<"assistant" | "editor">>;
  videoExpanded: boolean;
  setVideoExpanded: (bool: boolean) => void;
  showAssistant: boolean;
  onFileSelect: (file: File) => void;
  scratchIframe: MutableRefObject<HTMLIFrameElement | null>;
}

const ScratchEditorHeader: React.FC<ScratchEditorHeaderProps> = ({
  name,
  video,
  activeItem,
  setActiveItem,
  videoExpanded,
  setVideoExpanded,
  showAssistant,
  onFileSelect,
  scratchIframe,
}) => {
  // States definition
  const [videoThumbnail, setVideoThumbnail] = useState("");
  const [showVideo, setShowVideo] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  // State for controlling the onboarding glowing feature
  const [onboardGlowing, setOnboardGlowing] = useState(true);
  const [firstInstructionsOpening, setFirstInstructionsOpening] =
    useState(false);
  const [scratchTheme, setScratchTheme] = useState<"default" | "dark">(
    "default"
  );
  const [assistantPopupState, setAssistantPopupState] = useState<
    "closed" | "popup" | "popupNoCode" | "expanded" | "fullscreen"
  >("closed");
  const [assistantPopupClickedButton, setAssistantPopupClickedButton] =
    useState<AssistantAllButtonTypes>(undefined);

  // Get the platform context
  const { logo } = usePlatform();
  // Get the controller that defines the pointer behaviour of the Scratch Editor
  const {
    guiReady,
    setIsUnsavedProject,
    stepByStepInstructions,
    pdf,
    setShowUnsavedChangesPopUp,
    isUnsavedProject,
    currentCode,
    loadScratchCode,
  } = useContext(ScratchEnvironmentContext);

  // Editor context
  const {
    goToDashboard,
    AIAssistant,
  }: {
    goToDashboard: () => void;
    AIAssistant: {
      name: string;
      img: string;
      greeting: string;
      intro: string;
    };
  } = useContext(EditorContext);

  // Define a reference for the video iframe
  const videoIframeRef = useRef<HTMLIFrameElement>(null);
  // Define a reference for the player in order to avoid memory leaks
  const playerRef = useRef<any>(null);
  // Define a reference for the tab
  const tabRef = useRef<any>(null);
  // Define a reference for instructions button
  const instructionsButtonRef = useRef<any>(null);
  // Define a reference for the glowing button
  const buttonsContainerRef = useRef<HTMLDivElement>(null);

  // Get the parameters from the url
  const { id, moduleOrProject } = useParams();

  // Control the size of the video when resizing
  const [containerSize, imgContainerRef] = useElementSize<HTMLDivElement>();

  // Variables
  const hideVideo = activeItem === "assistant";

  useEffect(() => {
    if (videoExpanded) {
      const timer = setTimeout(() => {
        setShowVideo(true);
      }, 300);

      return () => clearTimeout(timer);
    } else {
      setShowVideo(false);
    }
  }, [videoExpanded]);

  /**
   * Function used for fetching video title and thumbnail
   */
  useEffect(() => {
    // Check if the video is defined
    if (video) {
      fetchVideoData(video).then((imageUrl: string) => {
        setVideoThumbnail(imageUrl);
      });
    }
  }, [video]);

  /*
   * Catch ctrl + s to run the code
   */
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === "s") {
        event.preventDefault();
        handleSaveClick();
      }
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  /**
   * Define the player reference once
   */
  useEffect(() => {
    if (videoIframeRef.current && !playerRef.current) {
      playerRef.current = new Player(videoIframeRef.current);
    }

    // Clean up when unmounting
    return () => {
      if (playerRef.current) {
        playerRef.current.unload(); // or destroy(), depending on the API
        playerRef.current = null;
      }
    };
  }, []);

  /**
   * Hook to stop the video if the AI assistant is fullscreen
   */
  useEffect(() => {
    if (assistantPopupState === "fullscreen") pauseVideo();
  }, [assistantPopupState]);

  /**
   * Function used to pause video
   */
  const pauseVideo = () => {
    if (playerRef.current) {
      const player = playerRef.current;
      player.pause();
    }
  };

  /**
   * Function that defines the contextual behaviour of the back button
   */
  const handleClickBack = () => {
    // Check if there's any unsaved information
    if (isUnsavedProject) {
      setShowUnsavedChangesPopUp(true);
      return;
    }

    if (activeItem === "assistant") setActiveItem("editor");
    else if (activeItem === "editor") goToDashboard();
  };

  const handleThumbnailClick = () => {
    // Start pausing the video
    pauseVideo();

    // Move the tab index to the beginning
    if (tabRef.current) tabRef.current.updatePillPosition(0);

    // Expanding the video
    setVideoExpanded(!videoExpanded);
  };

  /**
   * Function used to upload the file
   */
  const handleUploadChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) onFileSelect(file);

    // Reset input value so the same file can be uploaded again
    event.target.value = "";
  };

  /**
   * Function used to request save file
   */
  const handleSaveClick = () => {
    postScratchRequest(scratchIframe, "SAVE_PROJECT");

    // And release the unsaved status
    setIsUnsavedProject(false);
  };

  /**
   * Function used to execute a scratch file download when
   * clicking the download button
   */
  const handleDownload = () => {
    postScratchRequest(scratchIframe, "DOWNLOAD_PROJECT");
  };

  /**
   * Function used to execute a scratch file upload when
   * clicking the upload button
   */
  const handleUpload = () => {
    document.getElementById("file-input")?.click();
  };

  /**
   * Function used to restore the file to the default sprite
   */
  const handleRestoreSprite = () => {
    postScratchRequest(scratchIframe, "RESTORE_DEFAULT_SPRITE");
  };

  /**
   * Function used to toggle the turbo mode on Scratch
   */
  const handleToggleTurboMode = () => {
    postScratchRequest(scratchIframe, "TOGGLE_TURBO_MODE");
  };

  /**
   * Function used to request change the scratch theme
   */
  const handleChangeScratchTheme = () => {
    // Define the newTheme
    const newTheme = scratchTheme === "default" ? "dark" : "default";

    // Change the state
    setScratchTheme(newTheme);

    // And send the new theme to be changed
    postScratchRequest(scratchIframe, "CHANGE_THEME", { theme: newTheme });
  };

  const onAssistantClick = () => {
    // Move the tab index to the beginning
    if (tabRef.current) tabRef.current.updatePillPosition(0);

    // Get the current scratch code
    postScratchRequest(scratchIframe, "GET_JSON");
  };

  /**
   * Function executed when clicking the "Show me around button"
   */
  const handleShowMeAroundClick = () => {
    // First of all, check if the gui is ready to execute this
    if (!guiReady) return;

    // De-activate the glowing
    setOnboardGlowing(false);

    // Try to get the container pos to ensure that the "instructions button"
    // is moving to the beginning of the container
    let containerPos;
    if (buttonsContainerRef.current) {
      // Set the instruction button position for the animation
      const { x, y } = buttonsContainerRef.current.getBoundingClientRect();
      // And set the anchor position
      containerPos = { x, y };
    }

    // Force closing the instructions
    if (instructionsButtonRef.current) {
      // And close the PDF panel
      instructionsButtonRef.current.handleClosePDFPanel(containerPos);
    }

    // Force closing the video
    setVideoExpanded(false);

    // Activate the onboarding pop up
    setShowOnboarding(true);
  };

  const handleRewindCode = async () => {
    const file = await getScratchFileFromAWS(id || "", moduleOrProject || "");
    loadScratchCode(file);
  };

  /**
   * Open the instructions panel just after the first enter
   */
  useEffect(() => {
    if (firstInstructionsOpening)
      instructionsButtonRef.current &&
        instructionsButtonRef.current.setTriggerShowPDFPanel(true);
  }, [instructionsButtonRef, firstInstructionsOpening]);

  return (
    <>
      <div className="w-full h-[122px] pl-10 pr-12 relative inline-flex justify-between items-center">
        {/* Logo and back button */}
        <div className="inline-flex items-center gap-8">
          <div className="w-[124px] h-16 flex items-center justify-center">
            <img
              className="w-auto h-auto max-w-full max-h-full object-contain cursor-pointer"
              src={logo}
              onClick={goToDashboard}
              alt="Platform logo"
              role="main"
            />
          </div>
          <div className="w-px self-stretch bg-neutral-200" />
          <div className="justify-start items-center gap-6 inline-flex">
            <div
              id="scratch-back-button"
              className="flex-col justify-start items-center gap-2 inline-flex"
            >
              <div
                className="w-12 h-12 p-3 bg-neutral-50 hover:bg-neutral-100 ease-in-out duration-150 rounded-full justify-center items-center inline-flex cursor-pointer z-10"
                onClick={handleClickBack}
              >
                <MdClose className="w-6 h-6 fill-secondary-600" />
              </div>
              <div className="text-center text-secondary-600 text-xs font-semibold font-poppins uppercase leading-[14px] tracking-wide select-none">
                Close
              </div>
            </div>
            <div className="inline-flex flex-grow flex-col justify-center items-start gap-2 select-none">
              <h1 className="self-stretch text-neutral-600 text-[23px] font-bold font-sans leading-7">
                {name}
              </h1>
              <div ref={buttonsContainerRef} className="inline-flex gap-4">
                <AnimatePresence>
                  {/* Rewind button */}
                  {guiReady && (
                    <motion.div
                      key="rewind-button"
                      initial={{ width: "0px", opacity: 0, scale: 0.9 }}
                      animate={{ width: "100%", opacity: 1, scale: 1 }}
                      exit={{
                        width: "0%",
                        opacity: 0,
                        transition: { duration: 0.3, ease: "easeOut" },
                      }}
                      transition={{
                        width: { duration: 0.3, ease: "easeOut" },
                        opacity: {
                          duration: 0.3,
                          ease: "easeOut",
                          delay: 0.25,
                        },
                        scale: { duration: 0.3, ease: "easeOut", delay: 0.25 },
                      }}
                      className="p-1.5 w-full rounded-full bg-neutral-50 hover:bg-neutral-100 ease-in-out duration-150 flex justify-center items-center gap-2 cursor-pointer z-1 relative"
                      onClick={handleRewindCode}
                    >
                      <MdFastRewind className="w-4 h-4 fill-secondary-600" />
                    </motion.div>
                  )}
                  {!showOnboarding && guiReady && (
                    <motion.div
                      key="onboarding-button"
                      initial={{ width: "0px", opacity: 0, scale: 0.9 }}
                      animate={{ width: "100%", opacity: 1, scale: 1 }}
                      exit={{
                        width: "0%",
                        opacity: 0,
                        transition: { duration: 0.3, ease: "easeOut" },
                      }}
                      transition={{
                        width: { duration: 0.3, ease: "easeOut" },
                        opacity: {
                          duration: 0.3,
                          ease: "easeOut",
                          delay: 0.25,
                        },
                        scale: { duration: 0.3, ease: "easeOut", delay: 0.25 },
                      }}
                      // Open instructions once Scratch environment is opened
                      onAnimationComplete={() =>
                        setFirstInstructionsOpening(true)
                      }
                    >
                      <GlowingButton
                        from="#4EDBEA"
                        to="var(--color-secondary-400, #EA4EDB)"
                        glowing={onboardGlowing}
                      >
                        <div
                          className="h-[27px] px-3 py-1.5 w-full rounded-full bg-neutral-50 ease-in-out duration-150 flex justify-center items-center gap-2 cursor-pointer z-10 relative"
                          onClick={handleShowMeAroundClick}
                        >
                          <div className="text-secondary-600 text-sm font-semibold font-poppins leading-[17px] whitespace-nowrap">
                            Show Me Around!
                          </div>
                          <MdOutlineAutoAwesome className="w-4 h-4 fill-secondary-600" />
                        </div>
                      </GlowingButton>
                    </motion.div>
                  )}
                </AnimatePresence>
                {(stepByStepInstructions?.length || pdf) && (
                  <InstructionsButton
                    key="instructions-button"
                    ref={instructionsButtonRef}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Assistant and video buttons */}
        {activeItem === "editor" && (
          <div className="inline-flex items-center z-10">
            {/* Conditional rendering for Assistant */}
            <div className="flex space-x-2 mb-4 h-[80px]">
              <input
                aria-label="scratch-file-input"
                type="file"
                id="file-input"
                accept=".sb3"
                style={{ display: "none" }}
                onChange={handleUploadChange}
              />
              {/* File popup */}
              <PopupContainer
                id="scratch-file-button"
                icon={MdOutlineArticle}
                title="File"
              >
                <DropdownMenu
                  containerStyle="absolute w-[162px] top-0 right-2 p-3 -mt-3 bg-white rounded-lg shadow-card flex-col justify-center items-center gap-2 inline-flex cursor-pointer z-30"
                  optionsList={[
                    {
                      icon: <MdUploadFile className="w-full h-full" />,
                      text: "Upload",
                      onClickFunction: handleUpload,
                    },
                    {
                      icon: <MdDownload className="w-full h-full" />,
                      text: "Download",
                      onClickFunction: handleDownload,
                    },
                    {
                      icon: <MdOutlineSave className="w-full h-full" />,
                      text: "Save",
                      onClickFunction: handleSaveClick,
                    },
                  ]}
                />
              </PopupContainer>

              {/* Edit popup */}
              <PopupContainer
                id="scratch-edit-button"
                icon={MdOutlineEdit}
                title="Edit"
              >
                <DropdownMenu
                  containerStyle="absolute w-[162px] top-0 right-2 p-3 -mt-3 bg-white rounded-lg shadow-card flex-col justify-center items-center gap-2 inline-flex cursor-pointer z-30"
                  optionsList={[
                    {
                      icon: <MdRestore className="w-full h-full" />,
                      text: "Restore",
                      onClickFunction: handleRestoreSprite,
                    },
                    {
                      icon: <MdFastForward className="w-full h-full" />,
                      text: "Turbo Mode",
                      onClickFunction: handleToggleTurboMode,
                    },
                    // {
                    //   icon:
                    //     scratchTheme === "default" ? (
                    //       <MdOutlineDarkMode className="w-full h-full" />
                    //     ) : (
                    //       <MdOutlineLightMode className="w-full h-full" />
                    //     ),
                    //   text: `${
                    //     scratchTheme === "default" ? "Dark" : "Light"
                    //   } Mode`,
                    //   onClickFunction: handleChangeScratchTheme,
                    // },
                  ]}
                />
              </PopupContainer>
              {/* AI assistant */}
              {showAssistant && (
                <AssistantPopup
                  AIAssistant={AIAssistant}
                  state={assistantPopupState}
                  setState={setAssistantPopupState}
                  clickedButton={assistantPopupClickedButton}
                  setClickedButton={setAssistantPopupClickedButton}
                  onAssistantClick={onAssistantClick}
                  codeFiles={currentCode}
                  goToDashboard={goToDashboard}
                  language="Scratch"
                />
              )}
            </div>

            {/* Video placeholder */}
            {video !== "" && (
              <>
                <div className="w-6" />
                <div
                  id="scratch-video-view"
                  className="relative h-[94px] w-[152px]"
                >
                  {assistantPopupState !== "fullscreen" &&
                    (videoExpanded ? (
                      <AnimatePresence>
                        <motion.div
                          key="video-expanded"
                          className="cursor-pointer absolute overflow-hidden h-[94px] w-[152px] z-[15] rounded-lg"
                          initial={{
                            opacity: 0,
                            backdropFilter: "blur(0px)",
                            background: "rgb(0 0 0 / 10%)",
                            right: 100,
                            top: 200,
                          }}
                          animate={{
                            opacity: 1,
                            right: 0,
                            top: 0,
                            backdropFilter: "blur(2px)",
                          }}
                          exit={{
                            opacity: 0,
                            backdropFilter: "blur(0px)",
                            right: 100,
                            top: 200,
                          }}
                          onClick={handleThumbnailClick}
                        >
                          <motion.div className="h-[22px] px-3 py-1 bg-neutral-50/95 rounded shadow backdrop-blur-[32px] justify-center items-center gap-3 inline-flex right-1 top-1 absolute">
                            <div className="text-secondary-700 text-xs font-semibold font-poppins uppercase leading-[14px] tracking-wide select-none">
                              Show Code
                            </div>
                          </motion.div>
                          <MdCallReceived className="h-10 w-10 p-1 bg-gradient-to-tr bg-neutral-300 bg-opacity-50 rounded-tr rounded-bl-lg shadow backdrop-blur-xl justify-center gap-2 inline-flex left-0 bottom-0 absolute items-center fill-white" />
                        </motion.div>
                      </AnimatePresence>
                    ) : (
                      <AnimatePresence initial={false}>
                        <motion.div
                          key="code-expanded"
                          className={`cursor-pointer absolute overflow-hidden h-[94px] w-[152px] z-[15] rounded-lg`}
                          onClick={handleThumbnailClick}
                          initial={{
                            opacity: 0,
                            right: 100,
                            top: 200,
                          }}
                          animate={{
                            opacity: 1,
                            right: 0,
                            top: 0,
                          }}
                          exit={{
                            opacity: 0,
                            right: 100,
                            top: 200,
                          }}
                        >
                          <motion.div className="h-[22px] px-3 py-1 bg-neutral-50/95 rounded shadow backdrop-blur-[32px] justify-center items-center gap-3 inline-flex right-1 top-1 absolute">
                            <div className="text-secondary-700 text-xs font-semibold font-poppins uppercase leading-[14px] tracking-wide select-none">
                              Play Video
                            </div>
                          </motion.div>
                          <MdCallReceived className="h-10 w-10 p-1 bg-gradient-to-tr white rounded-tr rounded-bl-lg shadow backdrop-blur-sm justify-center gap-2 inline-flex left-0 bottom-0 absolute items-center fill-white" />
                        </motion.div>
                      </AnimatePresence>
                    ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Mobile thumbnail and video */}
      <motion.div
        ref={imgContainerRef}
        className={`absolute w-full h-screen items-center max-h-[calc(100vh-203px)] right-12 inline-flex ${
          hideVideo ? "hidden" : "block"
        }`}
        initial={{
          top: 14,
          width: 155,
          height: 94,
        }}
        animate={{
          top: videoExpanded ? 155 : 14,
          width: videoExpanded ? "calc(100% - 96px)" : 152,
          height: videoExpanded ? "calc(100vh - 203px)" : 94,
          right: videoExpanded ? undefined : 48,
          transition: { ease: "linear" },
        }}
      >
        <div
          className={`h-full w-full rounded-lg mx-auto relative inline-flex justify-center items-center ${
            videoExpanded ? "z-1" : ""
          }`}
        >
          <motion.div
            className={`h-full w-full ${showVideo ? "hidden" : "block"}`}
          >
            {videoThumbnail ? (
              <img
                className="h-full rounded-lg mx-auto"
                src={videoThumbnail}
                alt="Video Thumbnail"
              />
            ) : (
              video && (
                <div className="h-full w-full rounded-lg bg-neutral-100" />
              )
            )}
          </motion.div>
          <Video
            key="video"
            iframeRef={videoIframeRef}
            width={containerSize.width}
            className={`rounded-xl mx-auto overflow-hidden ${
              showVideo ? "block" : "hidden"
            }`}
            isOpen={true}
            videoUrl={video}
            maxHeight={containerSize.height}
          />
        </div>
      </motion.div>

      {/* Show me around instructions */}
      <AnimatePresence>
        {showOnboarding && (
          <OnboardingInstructions
            key="onboarding-instructions"
            scratchIframe={scratchIframe}
            setShowOnboarding={setShowOnboarding}
            afterFinishingFunction={() => setOnboardGlowing(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default ScratchEditorHeader;
